import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

// Inline-editable TanStack table cells shared by the admin tables

// Props shared by the editable cells (subset of TanStack's cell context)
const editableCellPropTypes = {
  getValue: PropTypes.func.isRequired,
  row: PropTypes.shape({
    original: PropTypes.shape({ _id: PropTypes.string.isRequired }).isRequired,
  }).isRequired,
  column: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
  onSave: PropTypes.func.isRequired,
};

// Editable text cell
export const EditableTextCell = ({ getValue, row, column, onSave }) => {
  const initialValue = getValue() ?? "";
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef(null);

  useEffect(() => {
    setValue(getValue() ?? "");
  }, [getValue]);

  const handleBlur = () => {
    if (value !== initialValue) {
      onSave(row.original._id, column.id, value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
    if (e.key === "Escape") {
      setValue(initialValue);
      e.target.blur();
    }
  };

  // Hidden mirror text sizes the input to its content
  return (
    <span className="inline-grid min-w-8">
      <span
        aria-hidden="true"
        className="invisible whitespace-pre col-start-1 row-start-1 px-1 text-sm"
      >
        {value || " "}
      </span>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="col-start-1 row-start-1 min-w-0 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-cyan-600 focus:outline-none w-full px-1 py-0.5 text-sm"
      />
    </span>
  );
};

EditableTextCell.propTypes = editableCellPropTypes;

// Editable number cell
export const EditableNumberCell = ({ getValue, row, column, onSave }) => {
  const initialValue = getValue() ?? 0;
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(getValue() ?? 0);
  }, [getValue]);

  const handleBlur = () => {
    const numValue = Number(value);
    if (numValue !== initialValue) {
      onSave(row.original._id, column.id, numValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.target.blur();
    }
    if (e.key === "Escape") {
      setValue(initialValue);
      e.target.blur();
    }
  };

  return (
    <input
      type="number"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className="bg-transparent border-b border-transparent hover:border-gray-300 focus:border-cyan-600 focus:outline-none w-20 px-1 py-0.5 text-sm text-right"
    />
  );
};

EditableNumberCell.propTypes = editableCellPropTypes;

// Editable checkbox cell
export const EditableCheckboxCell = ({ getValue, row, column, onSave }) => {
  const handleChange = (e) => {
    onSave(row.original._id, column.id, e.target.checked);
  };

  return (
    <input
      type="checkbox"
      checked={getValue() ?? false}
      onChange={handleChange}
      className="w-4 h-4 text-cyan-700 border-gray-300 rounded focus:ring-cyan-500 accent-cyan-700 cursor-pointer"
    />
  );
};

EditableCheckboxCell.propTypes = editableCellPropTypes;
